import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  fetchMeetings,
  fetchMeetingResponsibleOptions,
  reassignMeetingResponsible,
  scheduleMeeting,
  submitMeetingReport,
  type ScheduleMeetingPayload,
  type SubmitReportPayload,
} from "@/api/client360Meetings";

const STALE = 30_000;

/**
 * 3C — a meeting create / report mutation can touch the timeline, the meetings
 * list, the tasks tab (report → managed tasks), overview and the envelope.
 * Invalidate the same surface 3A does, plus the 3C meetings list.
 */
function invalidate3C(qc: ReturnType<typeof useQueryClient>, holdingId: string) {
  qc.invalidateQueries({ queryKey: ["client360Meetings", holdingId] });
  qc.invalidateQueries({ queryKey: ["client360Timeline", holdingId] });
  qc.invalidateQueries({ queryKey: ["clientTab", holdingId, "tasks"] });
  qc.invalidateQueries({ queryKey: ["client360Overview", holdingId] });
  qc.invalidateQueries({ queryKey: ["client360", holdingId] });
  qc.invalidateQueries({ queryKey: ["tasks"] });
  qc.invalidateQueries({ queryKey: ["homeSummary"] });
  // D-134 — meeting create/report/reassign must refresh the Meetings Queue list +
  // its filter counts from ANY entry point (page / topbar «+ Створити» / Client360),
  // not only when the page wraps the drawer with invalidateQueue. Prefix-invalidate
  // covers all ["meetingsQueue", scope, filter] variants (counts ride the response).
  qc.invalidateQueries({ queryKey: ["meetingsQueue"] });
}

export function useClient360Meetings(holdingId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ["client360Meetings", holdingId],
    queryFn: ({ signal }) => fetchMeetings(holdingId as string, signal),
    enabled: !!holdingId && enabled,
    staleTime: STALE,
  });
}

export function useScheduleMeeting(holdingId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: ScheduleMeetingPayload) => scheduleMeeting(holdingId, payload),
    onSuccess: () => invalidate3C(qc, holdingId),
  });
}

/** S4 — responsible-picker options (own/team-scoped, D3=A default). */
export function useMeetingResponsibleOptions(holdingId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ["meetingResponsibleOptions", holdingId],
    queryFn: ({ signal }) => fetchMeetingResponsibleOptions(holdingId as string, signal),
    enabled: !!holdingId && enabled,
    staleTime: STALE,
  });
}

/** S4 — reassign meeting responsible (reason REQUIRED). */
export function useReassignMeeting(holdingId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ interactionId, assignedUserId, reason }: { interactionId: string; assignedUserId: string; reason: string }) =>
      reassignMeetingResponsible(interactionId, { assignedUserId, reason }),
    onSuccess: () => invalidate3C(qc, holdingId),
  });
}

export function useSubmitMeetingReport(holdingId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ interactionId, body }: { interactionId: string; body: SubmitReportPayload }) =>
      submitMeetingReport(interactionId, body),
    onSuccess: () => invalidate3C(qc, holdingId),
  });
}
